import type { CSSProperties } from "react";

import {
	beatThemeTypographyImportant,
	readStyleScalar,
} from "../theme/tenantThemeCss";
import {
	isSiteSansFontFamilyToken,
	resolveTypographyFontFamilyForCss,
} from "../theme/typographyTokens";
import {
	cssColorDarkForStylesheet,
	cssColorForStylesheet,
	isDualColorValue,
} from "../ui/lib/color-mode-value";

/**
 * Keys on live editor `Element.styles` (flat) that map 1:1 onto `CSSProperties`.
 *
 * This is an allow-list: a key outside it is dropped from the published page with no error.
 * Exported so `responsiveOverridesCss` filters by the SAME list — the two used to disagree,
 * which is why `placeItems` looked intermittent (stripped from base, emitted from an override).
 */
export const FLAT_ELEMENT_STYLE_KEYS = [
	"alignContent",
	"alignItems",
	"backgroundColor",
	"backgroundImage",
	"backgroundPosition",
	"backgroundSize",
	"border",
	"borderColor",
	"borderRadius",
	"borderStyle",
	"borderWidth",
	"boxShadow",
	"color",
	"cursor",
	"display",
	"float",
	"flexDirection",
	"flexWrap",
	"fontFamily",
	"fontSize",
	"fontStyle",
	"fontWeight",
	"gap",
	"gridGap",
	"gridAutoFlow",
	"gridTemplateColumns",
	"gridTemplateRows",
	"height",
	"justifyContent",
	"justifyItems",
	"placeItems",
	"placeContent",
	"letterSpacing",
	"lineHeight",
	"margin",
	"marginBottom",
	"marginLeft",
	"marginRight",
	"marginTop",
	"maxHeight",
	"maxWidth",
	"minHeight",
	"minWidth",
	"objectFit",
	// Declared on ElementStyles and previously emitted from responsive overrides only.
	// Phase 12.2 unified the two paths, so it must be here or it would regress.
	"opacity",
	"objectPosition",
	"overflow",
	"padding",
	"paddingBottom",
	"paddingLeft",
	"paddingRight",
	"paddingTop",
	"right",
	"position",
	"left",
	"top",
	"bottom",
	"textAlign",
	"textDecoration",
	"textTransform",
	"backgroundClip",
	"WebkitBackgroundClip",
	"WebkitTextFillColor",
	"transform",
	"transition",
	"animation",
	"width",
	"zIndex",
] as const;

const COLOR_STYLE_KEYS = new Set<string>([
	"backgroundColor",
	"borderColor",
	"color",
	"WebkitTextFillColor",
]);

function kebabCase(prop: string): string {
	return prop.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
}

function resolvedStyleString(key: string, value: string): string {
	if (COLOR_STYLE_KEYS.has(key) && isDualColorValue(value)) {
		return cssColorForStylesheet(value);
	}
	if (key === "fontFamily") {
		return resolveTypographyFontFamilyForCss(value);
	}
	return value;
}

/**
 * Live editor elements store settings as flat `styles`.
 * Typography props Theme Manager forces with `!important` are sanitized for
 * React inline styles (no `!important` suffix — browsers reject it).
 */
export function flatElementStylesToReactCss(
	raw: Record<string, unknown> | undefined
): CSSProperties {
	if (!raw || typeof raw !== "object") {
		return {};
	}
	const out: CSSProperties = {};
	for (const key of FLAT_ELEMENT_STYLE_KEYS) {
		const stored = raw[key];
		const v =
			typeof stored === "string"
				? stored
				: typeof stored === "object" && stored !== null
					? readStyleScalar(stored)
					: "";
		if (v.trim()) {
			if (key === "fontFamily") {
				const family = resolveTypographyFontFamilyForCss(v);
				// Site Sans is region Theme Manager basis — omit so tag rules apply.
				if (isSiteSansFontFamilyToken(family)) {
					continue;
				}
				(out as Record<string, string>)[key] = family;
			} else {
				(out as Record<string, string>)[key] = resolvedStyleString(key, v);
			}
		}
	}
	if (typeof raw.opacity === "string" && raw.opacity.trim()) {
		const n = Number(raw.opacity);
		if (!Number.isNaN(n)) {
			out.opacity = n;
		}
	}
	const layoutAlign = raw.layoutAlign;
	if (layoutAlign === "center") {
		out.marginLeft = "auto";
		out.marginRight = "auto";
	} else if (layoutAlign === "right") {
		out.marginLeft = "auto";
	}
	return beatThemeTypographyImportant(
		out as Record<string, unknown>
	) as CSSProperties;
}

export type ImportantStyleDecls = {
	dark: string;
	light: string;
};

/**
 * Declaration blocks for a flat ElementStyles bag. Dual Light|Dark colors split
 * into `light` (base) and `dark` (`.dark` / canvas dark mode).
 */
export function flatElementStylesToImportantDecls(
	raw: Record<string, unknown> | undefined
): ImportantStyleDecls {
	const lightParts: string[] = [];
	const darkParts: string[] = [];
	if (!raw || typeof raw !== "object") {
		return { light: "", dark: "" };
	}
	for (const key of FLAT_ELEMENT_STYLE_KEYS) {
		const stored = raw[key];
		const v =
			typeof stored === "string"
				? stored
				: typeof stored === "object" && stored !== null
					? readStyleScalar(stored)
					: "";
		if (!v.trim()) {
			continue;
		}
		if (key === "fontFamily") {
			const family = resolveTypographyFontFamilyForCss(v);
			if (isSiteSansFontFamilyToken(family)) {
				continue;
			}
			lightParts.push(`${kebabCase(key)}:${family}!important`);
			continue;
		}
		if (COLOR_STYLE_KEYS.has(key) && isDualColorValue(v)) {
			const light = cssColorForStylesheet(v);
			if (light) {
				lightParts.push(`${kebabCase(key)}:${light}!important`);
			}
			const dark = cssColorDarkForStylesheet(v);
			if (dark) {
				darkParts.push(`${kebabCase(key)}:${dark}!important`);
			}
			continue;
		}
		lightParts.push(`${kebabCase(key)}:${v.trim()}!important`);
	}
	return { light: lightParts.join(";"), dark: darkParts.join(";") };
}
